import { React, useState, useEffect } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { CreateValidateTextField } from "..";
import {
  Dialog,
  DialogTitle,
  Snackbar,
  Alert,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";

function CompanyEdit(props) {
  const { updateTable, setUpdateTable, company, memberships, statuses } = props;

  const [openEditDialog, setOpenEditDialog] = useState(false);

  const [name, setName] = useState("");
  const [membership, setMembership] = useState("");
  const [status, setStatus] = useState("");
  const [pcEmail, setPCEmail] = useState("");
  const [pcName, setPCName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [billingDate, setBillingDate] = useState("");
  const [paymentDate, setPaymentDate] = useState("");

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  useEffect(() => {
    if (company) {
      setName(company.name || "");
      setMembership(company.membership?.membershipId || "");
      setStatus(company.status?.statusId || "");
      setPCEmail(company.primaryContact.email || "");
      setPCName(company.primaryContact.name || "");
      setStartDate(company.startDate || "");
      setEndDate(company.endDate || "");
      setBillingDate(company.billingDate || "");
      setPaymentDate(company.paymentDate || "");
    }
  }, [company, openEditDialog]);

  const handleEditCompany = (event) => {
    event.preventDefault();

    if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
      setSnackbarMessage("End date must be after start date.");
      setSnackbarOpen(true);
      return;
    }

    if (
      billingDate &&
      paymentDate &&
      new Date(paymentDate) < new Date(billingDate)
    ) {
      setSnackbarMessage("Payment date must be after billing date.");
      setSnackbarOpen(true);
      return;
    }

    const body = new FormData();
    body.append("employerId", company.employerId);
    body.append("name", name);
    body.append("membershipId", membership);
    body.append("statusId", status);
    body.append("pcEmail", pcEmail);
    body.append("pcName", pcName);
    body.append("startDate", startDate);
    body.append("endDate", endDate);
    body.append("billingDate", billingDate);
    body.append("paymentDate", paymentDate);

    axios.post("/company/editCompany", body).then((res) => {
      const data = res.data;
      if (data.success) {
        resetHooks();
        setOpenEditDialog(false);
        setUpdateTable(!updateTable);
      } else {
        setSnackbarMessage(data.message);
        setSnackbarOpen(true);
      }
    });
  };

  const resetHooks = () => {
    setName("");
    setMembership("");
    setStatus("");
    setPCEmail("");
    setPCName("");
    setStartDate("");
    setEndDate("");
    setBillingDate("");
    setPaymentDate("");
  };

  function showEditDialog(open) {
    return (
      <Dialog open={open} maxWidth={false}>
        <DialogTitle className="CompanyEdit-title mx-auto text-2xl font-bold font-brand-primary">
          Edit Company Information
        </DialogTitle>
        <div className="CompanyEdit-modalContainer flex flex-col w-fit mb-4 mx-8">
          <div className="CompanyEdit-nameContainer flex flex-col w-96 mx-auto mb-2">
            <div className="CompanyEdit-name text-lg font-brand-primary">
              Company Name *
            </div>
            <CreateValidateTextField
              placeholder="Microsoft"
              pattern="(?=.*\w).+"
              value={name}
              setter={setName}
              errorMessage={"Invalid Name."}
              required={true}
            />
          </div>

          <div className="CompanyEdit-membershipContainer flex flex-col w-96 mx-auto mb-2">
            <InputLabel className="text-lg font-brand-primary">
              Partnership *
            </InputLabel>
            <FormControl fullWidth>
              <Select
                value={membership}
                onChange={(e) => setMembership(e.target.value)}
                required>
                {memberships.map((mem) => (
                  <MenuItem key={mem.membershipId} value={mem.membershipId}>
                    {mem.membershipType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className="CompanyEdit-statusContainer flex flex-col w-96 mx-auto mb-2">
            <InputLabel className="text-lg font-brand-primary">
              Status *
            </InputLabel>
            <FormControl fullWidth>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                required>
                {statuses.map((stat) => (
                  <MenuItem key={stat.statusId} value={stat.statusId}>
                    {stat.status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          
          <div className="CompanyEdit-pcEmailContainer flex flex-col w-96 mx-auto mb-2">
            <div className="CompanyEdit-pcEmail text-lg font-brand-primary">
              Primary Contact&#39;s Email *
            </div>
            <CreateValidateTextField
              placeholder="example@example.com"
              pattern="[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}"
              value={pcEmail}
              setter={setPCEmail}
              errorMessage={"Invalid Email."}
              required={true}
            />
          </div>

          <div className="CompanyEdit-pcNameContainer flex flex-col w-96 mx-auto mb-2">
            <div className="CompanyEdit-pcName text-lg font-brand-primary">
              Primary Contact&#39;s Name *
            </div>
            <CreateValidateTextField
              placeholder="Kevin McCallister"
              pattern="(?=.*\w).+"
              value={pcName}
              setter={setPCName}
              errorMessage={"Invalid Name."}
              required={true}
            />
          </div>


          {[
            { label: "Start Date", value: startDate, setter: setStartDate },
            { label: "End Date", value: endDate, setter: setEndDate },
            {
              label: "Billing Date",
              value: billingDate,
              setter: setBillingDate,
            },
            {
              label: "Payment Date",
              value: paymentDate,
              setter: setPaymentDate,
            },
          ].map((field, idx) => (
            <div
              key={idx}
              className="CompanyEdit-dateContainer flex flex-col w-96 mx-auto mb-2">
              <label className="text-lg font-brand-primary">
                {field.label}
              </label>
              <input
                type="date"
                className="border p-2 rounded"
                value={field.value}
                onChange={(e) => field.setter(e.target.value)}
              />
            </div>
          ))}

          <div className="CompanyEdit-buttonContainer flex justify-between px-8 mt-4">
            <button
              type="button"
              className="CompanyEdit-updateButton primary-button bg-brand-quaternary mb-2 h-11 pt-3 mr-4"
              onClick={handleEditCompany}>
              Update Company
            </button>

            <button
              type="button"
              className="CompanyEdit-cancelButton primary-button bg-brand-primary mb-2 h-11 pt-3"
              onClick={() => setOpenEditDialog(false)}>
              Cancel
            </button>
          </div>
        </div>

        <Snackbar
          open={snackbarOpen}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          autoHideDuration={6000}
          onClose={() => {
            setSnackbarOpen(false);
            setSnackbarMessage("");
          }}>
          <Alert
            onClose={() => {
              setSnackbarOpen(false);
              setSnackbarMessage("");
            }}
            severity="error"
            sx={{ width: "100%" }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Dialog>
    );
  }

  return (
    <div className="CompanyEdit-container">
      {showEditDialog(openEditDialog)}
      <button className="flex mr-4" onClick={() => setOpenEditDialog(true)}>
        <EditIcon fontSize="large" className="fill-brand-quaternary" />
      </button>
    </div>
  );
}

CompanyEdit.propTypes = {
  updateTable: PropTypes.bool,
  setUpdateTable: PropTypes.func,
  company: PropTypes.shape({
    employerId: PropTypes.number,
    name: PropTypes.string,
    membership: PropTypes.string,
    status: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    billingDate: PropTypes.string,
    paymentDate: PropTypes.string,
    primaryContact: PropTypes.shape({
      email:PropTypes.string,
      name: PropTypes.string
    }),
  }),
  memberships: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })
  ),
  statuses: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })
  ),
};

CompanyEdit.defaultProps = {
  updateTable: false,
  setUpdateTable: () => {},
  company: {},
  memberships: [],
  statuses: [],
};

export default CompanyEdit;
